import React, { useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { CloseButton, CancelButton, StandardButton } from '@/styles/StandardButtons';
import { colors } from '@/design-system/colors';
import { UploadCloud } from 'lucide-react';
import { BrowseDriveModal } from '@/components/shared/BrowseDriveModal';
import { useProject } from '@/components/ProjectSelector';
import '@/design-system/forms.css';
import '@/design-system/typography.css';

/**
 * Generic "Upload Files" popup: pure file-selection UI, reusable from any
 * module. Built the same way every other modal in this app is built
 * (`GenericForm.tsx`'s own pattern: `createPortal` to `document.body` +
 * the shared `.generic-form-*` header/body/footer classes from
 * `design-system/forms.css`), NOT a separate Radix Dialog component --
 * so the header/close button/footer button layout match every other
 * modal exactly, and so the overlay-click check uses the same
 * `e.target === e.currentTarget` pattern already established as correct
 * (a hand-rolled `.contains()` check is what caused the "Cancel closes
 * the parent modal too" bug found earlier in ProposalsForm).
 *
 * Deliberately contains NO entity-specific logic: it only hands back the
 * `File[]` the user picked via `onConfirm`. Staging those files
 * temporarily is `useAttachmentStaging`'s job; persisting them once an
 * entity id exists is each module's own upload service's job. This
 * component never calls either -- see ARCHITECTURE note in
 * `useAttachmentStaging.ts`.
 *
 * "Browse Drive" opens the shared BrowseDriveModal for the project in
 * `driveProjectId` (default: the globally selected project) and hands the
 * picked Drive files back through the SAME `onConfirm(File[])` as a local
 * pick (as Drive stand-ins, lib/drive-file-ref.ts) -- so every caller gets
 * it at once and none needs to know about it.
 */
interface GenericFileUploadModalProps {
  isOpen: boolean;
  onClose: () => void;
  /** Called with the files the user picked (via drag&drop or Browse Files). Does not close the modal itself -- the caller decides (usually closes right after staging them). */
  onConfirm: (files: File[]) => void;
  title?: string;
  accept?: string;
  multiple?: boolean;
  /** The project whose Drive "Browse Drive" shows. Omitted = the globally selected project; '' = none yet (the popup asks for one);
   *  null = this kind of record has no project (a person's documents), so there is no Drive to browse and the button is disabled. */
  driveProjectId?: string | null;
}

export function GenericFileUploadModal({
  isOpen,
  onClose,
  onConfirm,
  title = 'Upload Files',
  accept,
  multiple = true,
  driveProjectId,
}: GenericFileUploadModalProps) {
  const [isDragActive, setIsDragActive] = useState(false);
  const [isDriveOpen, setIsDriveOpen] = useState(false);
  const { selectedProject } = useProject();
  const fileInputRef = useRef<HTMLInputElement>(null);

  if (!isOpen) return null;

  const handleFiles = (fileList: FileList | null) => {
    if (!fileList || fileList.length === 0) return;
    onConfirm(Array.from(fileList));
    onClose();
  };

  const handleDrop = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setIsDragActive(false);
    handleFiles(e.dataTransfer.files);
  };

  const handleOverlayClick = (e: React.MouseEvent) => {
    if (e.target === e.currentTarget) onClose();
  };

  const modalContent = (
    <div
      className="generic-form-overlay"
      style={{ top: 0, left: 0, right: 0, bottom: 0 }}
      onClick={handleOverlayClick}
    >
      <div
        className="generic-form-container w-full max-w-lg"
        style={{ maxHeight: 'var(--form-max-height-compact)' }}
      >
        {/* Header -- same markup/classes as every other modal in the app */}
        <div
          className="generic-form-header generic-form-header-compact"
          style={{ backgroundColor: colors.formHeaderFooter }}
        >
          <h2 className="font-form-title">{title}</h2>
          <CloseButton onClick={onClose} />
        </div>

        {/* Body */}
        <div className="generic-form-body generic-form-body-compact">
          <div
            onDragOver={(e) => { e.preventDefault(); setIsDragActive(true); }}
            onDragLeave={() => setIsDragActive(false)}
            onDrop={handleDrop}
            className={`flex flex-col items-center justify-center gap-3 rounded-lg border-2 border-dashed p-10 text-center transition-colors ${
              isDragActive ? 'border-gray-500 bg-gray-50' : 'border-gray-300'
            }`}
          >
            <UploadCloud className="h-10 w-10 text-gray-400" />
            <p className="text-sm text-gray-600">Drop files here</p>
            <input
              ref={fileInputRef}
              type="file"
              accept={accept}
              multiple={multiple}
              className="hidden"
              onChange={(e) => {
                handleFiles(e.target.files);
                if (fileInputRef.current) fileInputRef.current.value = '';
              }}
            />
          </div>
        </div>

        {/* Footer -- all buttons bottom-right, same .generic-form-footer
            (justify-content: flex-end) every other modal uses. Browse
            Files sits between Cancel and Browse Drive; it triggers the
            exact same hidden <input type="file"> above, just relocated
            visually. */}
        <div
          className="generic-form-footer generic-form-footer-compact"
          style={{ backgroundColor: colors.formHeaderFooter }}
        >
          <CancelButton onClick={onClose} />
          <StandardButton customVariant="blue" type="button" onClick={() => fileInputRef.current?.click()}>
            Browse Files
          </StandardButton>
          <StandardButton customVariant="blue" type="button" disabled={driveProjectId === null} title={driveProjectId === null ? 'Not available for this record' : undefined} onClick={() => setIsDriveOpen(true)}>
            Browse Drive
          </StandardButton>
        </div>
      </div>
      <BrowseDriveModal
        isOpen={isDriveOpen}
        onClose={() => setIsDriveOpen(false)}
        projectId={driveProjectId === undefined ? selectedProject : driveProjectId}
        onConfirm={(files) => { onConfirm(files); onClose(); }}
      />
    </div>
  );

  return createPortal(modalContent, document.body);
}
